import React, { useEffect, useRef } from 'react'

import { useOpacityToggle, useScaleToFit, useFormatMoney } from '@hooks'
import {
    useAmuseBoostStore,
    useBetSliderStore,
    useBuyBonusStore,
    useConfigurationStore,
    useCountdownFSBonusStore,
    useDoubleChanceStore,
    useFreeBuyBonusStore,
    useFreeSpinsBonusesStore,
    useLuckySpinsStore,
    useNavigationStore,
    useSpinButtonSectionStore,
} from '@stores'
import { withAutoBlur } from '@utils/misc'
import { StrokedGradientText } from '@utils/StrokedGradientText'
import {
    ChangeOpacityComponents,
    CollectClickedMessage,
    DoubleChanceButtonClickedMessage,
    GameUIMessageScope,
    GameUIMessageType,
} from 'game-ui-types'

import styles from './DoubleChanceButton.module.css'
import { getBetValue } from '@utils/betOption'
import { cn } from '@amusnet/react-ui'
import { useAmuseBoostPromoWidgetPositions } from '@hooks/useAmuseBoostPromoWidgetPositions'

interface DoubleChanceButtonProps {
    isCompact?: boolean
}

const DoubleChanceButton: React.FC<DoubleChanceButtonProps> = ({ isCompact }) => {
    const formatMoney = useFormatMoney()
    const viewport = useConfigurationStore((s) => s.viewport)
    const doubleChanceMultiplier = useConfigurationStore((s) => s.doubleChanceMultiplier)
    const currency = useConfigurationStore((s) => s.currency)
    const betOptions = useConfigurationStore((s) => s.betOptions)

    const isAutoplaySectionOpened = useSpinButtonSectionStore((s) => s.isAutoplaySectionOpened)
    const isBetSectionOpened = useSpinButtonSectionStore((s) => s.isBetSectionOpened)
    const isLongPress = useSpinButtonSectionStore((s) => s.isLongPress)
    const isSpaceKeyPressed = useSpinButtonSectionStore((s) => s.isSpaceKeyPressed)

    const isAmuseBoostModalOpen = useAmuseBoostStore((s) => s.isAmuseBoostModalOpen)
    const setIsAmuseBoostModalOpen = useAmuseBoostStore((s) => s.setIsAmuseBoostModalOpen)

    const setIsDoubleChanceActivated = useDoubleChanceStore((s) => s.setIsDoubleChanceActivated)
    const isDoubleChanceActivated = useDoubleChanceStore((s) => s.isDoubleChanceActivated)

    const updateCurrentFeatureMultiplier = useBuyBonusStore((s) => s.updateCurrentFeatureMultiplier)

    const isFSBModalOpened = useFreeSpinsBonusesStore((s) => s.isModalOpened)
    const isFBBModalOpened = useFreeBuyBonusStore((s) => s.isModalOpened)
    const isLSBModalOpened = useLuckySpinsStore((s) => s.isModalOpened)
    const isCFSBModalOpened = useCountdownFSBonusStore((s) => s.isModalOpened)

    const hasChancePlus = useBuyBonusStore((s) => s.hasChancePlus)

    const activeIndex = useBetSliderStore((s) => s.activeIndex)
    const showModal = useBuyBonusStore((s) => s.showModal)

    const isMenuOpen = useNavigationStore((s) => s.isMenuOpen)

    const { secondaryButtonPosition: dcButtonPosition } = useAmuseBoostPromoWidgetPositions()

    const dcButtonStyles = useConfigurationStore((s) => s.getComponentConfig('DoubleChanceButton').styles)
    const dcButtonDimensions = useConfigurationStore((s) => s.getComponentConfig('DoubleChanceButton').dimensions)

    const dcCompactButtonStyles = useConfigurationStore((s) => s.getComponentConfig('DoubleChanceButtonCompact').styles)
    const dcCompactButtonDimensions = useConfigurationStore(
        (s) => s.getComponentConfig('DoubleChanceButtonCompact').dimensions
    )
    const dcCompactButtonPosition = useConfigurationStore(
        (s) => s.getComponentConfig('DoubleChanceButtonCompact').position
    )

    const doubleChanceButtonPosition = {
        ...(isCompact ? dcCompactButtonPosition : dcButtonPosition),
    }

    const doubleChanceButtonDimensions = {
        ...(isCompact ? dcCompactButtonDimensions : dcButtonDimensions),
    }
    const doubleChanceComponent = useRef<HTMLButtonElement | null>(null)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const moneyContainerRef = useRef<HTMLDivElement | null>(null)
    const labelContainerRef = useRef<HTMLDivElement | null>(null)

    const gradientStyle =
        'bg-[linear-gradient(180deg,#93490B_0%,#FEE880_36%,#FFFBE6_51%,#FEE880_63%,#93490B_100%)] bg-clip-text text-transparent'
    const betValue = getBetValue(betOptions[activeIndex]) * doubleChanceMultiplier

    const { isComponentInteractive, opacity } = useOpacityToggle(
        ChangeOpacityComponents.DOUBLE_CHANCE_BUTTON,
        doubleChanceComponent,
        0,
        false
    )

    const isBonusModalOpenedRef =
        showModal || isFBBModalOpened || isFSBModalOpened || isLSBModalOpened || isCFSBModalOpened
    const activeConditions =
        isComponentInteractive && !showModal && (isLongPress || isSpaceKeyPressed) && !isBonusModalOpenedRef

    const scaleMoney = useScaleToFit(containerRef, moneyContainerRef, [
        activeIndex,
        isDoubleChanceActivated,
        viewport,
        isAmuseBoostModalOpen,
        isCompact,
    ])
    const scaleLabel = useScaleToFit(containerRef, labelContainerRef, [
        activeIndex,
        isDoubleChanceActivated,
        viewport,
        isAmuseBoostModalOpen,
        isCompact,
    ])

    const handleButtonClicked = () => {
        setIsDoubleChanceActivated(!isDoubleChanceActivated)
        setIsAmuseBoostModalOpen(false)

        const doubleChanceClickMessage: DoubleChanceButtonClickedMessage = {
            type: GameUIMessageType.DOUBLE_CHANCE_BUTTON_CLICKED,
            scope: GameUIMessageScope.DOUBLE_CHANCE,
        }

        updateCurrentFeatureMultiplier(!isDoubleChanceActivated ? doubleChanceMultiplier : 1)

        window.postMessage(doubleChanceClickMessage)

        const collectClickedMessage: CollectClickedMessage = {
            type: GameUIMessageType.COLLECT_CLICKED,
        }

        window.postMessage(collectClickedMessage, '*')
    }

    useEffect(() => {
        updateCurrentFeatureMultiplier(isDoubleChanceActivated ? doubleChanceMultiplier : 1)
    }, [isDoubleChanceActivated])

    return (
        <button
            id="double-chance-button"
            ref={doubleChanceComponent}
            className={cn(
                styles.buttonBase,
                isDoubleChanceActivated
                    ? hasChancePlus
                        ? styles.chancePlusOn
                        : styles.on
                    : isCompact
                      ? styles.compact
                      : styles.off,
                isCompact ? [dcCompactButtonStyles, 'p-[10px]'] : dcButtonStyles
            )}
            onClick={withAutoBlur(handleButtonClicked)}
            style={{
                ...doubleChanceButtonDimensions,
                ...doubleChanceButtonPosition,
                zIndex: isAutoplaySectionOpened || isBetSectionOpened || isBonusModalOpenedRef || isMenuOpen ? 10 : 11,
                opacity: activeConditions ? 0.5 : opacity,
                pointerEvents: activeConditions ? 'none' : 'auto',
            }}
        >
            <div
                id="double-chance-button-text"
                ref={containerRef}
                className={cn(
                    'flex flex-col items-center h-[105px]',
                    hasChancePlus ? 'justify-end gap-y-[6px] pb-[15px]' : 'justify-around'
                )}
                style={{
                    width: isCompact ? '110px' : '130px',
                }}
            >
                <div
                    ref={labelContainerRef}
                    style={{
                        zoom: scaleLabel,
                    }}
                    className="flex flex-col items-center justify-center leading-[0.8] font-black mt-[10px]"
                >
                    {hasChancePlus ? (
                        <StrokedGradientText
                            textClassName={styles.chanceTextCompressed}
                            gradientClassName={gradientStyle}
                        >
                            CHANCE+
                        </StrokedGradientText>
                    ) : (
                        <>
                            <div className={cn('text-[53px] leading-[0.8]', gradientStyle)}>x2</div>
                            <div className={cn('text-[29px] leading-none', gradientStyle)}>CHANCE</div>
                        </>
                    )}
                </div>

                <div
                    ref={moneyContainerRef}
                    style={{
                        zoom: scaleMoney,
                    }}
                    className="leading-[0.8] overflow-hidden whitespace-nowrap text-white text-center text-[30px] font-bold [text-shadow:1px_2px_1px_#000000]"
                >
                    {isDoubleChanceActivated ? (
                        <div>ACTIVE</div>
                    ) : (
                        <div className="flex items-baseline gap-[2px]">
                            <div>{formatMoney(betValue)}</div>
                            <div className="text-[16px] uppercase">{currency}</div>
                        </div>
                    )}
                </div>
            </div>
        </button>
    )
}

export default DoubleChanceButton
